import styles from 'styles/shop/product.module.css';
import PropTypes from 'prop-types';
import { useLanguageLoader } from 'contexts/language';
import { useCart } from 'hooks/cart';
import { CustomError } from 'components/error/Error';
import { useSyncShop } from 'hooks/shop';
import { TopBar } from 'components/store/TopBar';
import { Container } from 'components/layout/Container';
import { BorderBottom } from 'components/layout/BorderBottom';
import { Footer } from 'components/store/Footer';
import { LocalCartProvider } from 'providers/localCart';
import { EVENT_TYPES } from 'ui/lib/pixels';
import ProductSeoSchema from 'components/seo/ProductSeoSchema';
import LazyLoad from 'react-lazyload';
import { Formik } from 'formik';
import { getInitialValues } from 'components/products/utils';
import ProductProvider, { useProductContext } from 'components/products/context';
import { useIsDesktop } from 'contexts/profile';
import {
  BuyButton,
  ColorBox,
  CustomizationPopUp,
  ProductDetails,
  ProductSlides,
  QuantityBox,
  RatingSection,
  SimilarProducts,
  VariantBox,
} from 'components/products';
import cx from 'classnames';
import { useQueryParams } from 'hooks/location';
import Skeleton from 'components/store/common/Skeleton';
import React, { useRef } from 'react';
import { useTrackGa } from 'hooks/ga';

function Product() {
  const {
    product,
    shop
  } = useProductContext();
  const lang = useLanguageLoader();
  const isDesktop = useIsDesktop();
  const [, addToCart] = useCart([]);
  const params = useQueryParams();

  useSyncShop(shop, {
    events: [[EVENT_TYPES.PRODUCT_VIEWED]],
  });

  useTrackGa(product._id, 'product', shop._id);

  const ref = useRef();

  const onSubmit = async (values, helpers) => {
    const payload = {
      note: values.note,
      quantity: values.quantity,
      variantId: values.variantId,
      color: values.colorId
    };
    await addToCart(shop._id, payload);
    helpers.resetForm({
      ...values,
      note: '',
      quantity: product?.quantity?.minQuantity ?? 1,
    });
  };

  return (
    <div
      className={styles.container}
    >
      <ProductSeoSchema
        product={product}
        shop={shop}
      />
      <TopBar shop={shop} showTabs={false}/>
      <Container>
        <div
          className={styles.content}
        >
          <Formik
            initialValues={getInitialValues(product, params.get('color'))}
            onSubmit={onSubmit}
            enableReinitialize={true}
          >
            <div className={styles.main}>
              <div className={styles.left}>
                <div
                  className={cx({
                    [styles.fixed]: isDesktop,
                  })}
                >
                  <ProductSlides zoomRef={ref} key={isDesktop} />
                  {isDesktop && (
                    <BuyButton/>
                  )}
                </div>
              </div>
              <div ref={ref} className={styles.zoom}></div>
              <div className={styles.right}>
                <ProductDetails/>
                <VariantBox/>
                <ColorBox/>
                <CustomizationPopUp/>
                <QuantityBox/>
                {!isDesktop && (
                  <BuyButton/>
                )}
                {product?.description && (
                  <>
                    <div className={styles.key1}>
                      {lang('store.headings.productDescription')}
                    </div>
                    <div
                      className={styles.desc}
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    >
                    </div>
                  </>
                )}
              </div>
            </div>
          </Formik>
          {product.rating && +product.rating?.avg > 0 && (
            <LazyLoad
              once={true}
              height={300}
              offset={100}
              placeholder={<Skeleton height={300}/>}
            >
              <RatingSection/>
            </LazyLoad>
          )}
          <LazyLoad
            once={true}
            height={300}
            offset={100}
            placeholder={<Skeleton height={300}/>}
          >
            <SimilarProducts/>
          </LazyLoad>
        </div>
      </Container>
      <BorderBottom/>
      <Footer shop={shop}/>
    </div>
  );
}

export default function ProductPage({
  product,
  shop,
  errorCode,
}) {
  if (errorCode) {
    return <CustomError code={errorCode}/>;
  }
  return (
    <ProductProvider product={product} shop={shop}>
      <LocalCartProvider shop={shop}>
        <Product/>
      </LocalCartProvider>
    </ProductProvider>
  );
}

ProductPage.defaultProps = {
  errorCode: null,
};

ProductPage.propTypes = {
  product: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  errorCode: PropTypes.number,
};
